import { zipObject } from 'lodash';
import { CallsheetApi } from '../../../api/callsheet/callsheet.api';
import { SystemEffect } from '../../internal/MessageService/_private/SystemEffectMessage';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { CallsheetMutationService } from './CallsheetMutationService';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithServices } from '../../../platform/ts/services';
import { WithCallsheetService } from '../CallsheetService/CallsheetService';
import { WithProductionService } from '../ProductionService/ProductionService';
import { WithBookingService } from '../BookingService/BookingService';
import {
  generateDuplicateCallsheetData,
  DuplicateCallsheetInput
} from './_private/generateDuplicateCallsheetData';
import { extractBookingIdsFromProductions } from '../CallsheetService/_private/generateCallsheetDataFromProductions';
import { CallsheetDateSelection } from 'v5/features/callsheets/types';

export function createCallsheetMutationService(
  config: WithServices<
    [
      WithErrorService,
      WithMessageService,
      WithCallsheetService,
      WithProductionService,
      WithBookingService
    ]
  > & {
    api: CallsheetApi;
  }
): CallsheetMutationService {
  const {
    api,
    services: {
      errorService,
      messageService,
      callsheetService,
      productionService,
      bookingService
    }
  } = config;
  const interceptor = errorService.withCategory('CallsheetMutationService');

  async function reportEffectAndWaitUntilCacheReady(
    scope: SystemEffect[] = []
  ) {
    await messageService.reportSystemEffect.sendRequest(
      {
        scope
      },
      3000
    );
  }

  return {
    createCallsheet: async (workspace, request) => {
      const result = await interceptor.wrap(() =>
        api.createCallsheet(workspace, request)
      )();
      await reportEffectAndWaitUntilCacheReady([
        {
          entity: 'callsheet',
          entityId: result.id,
          payload: { type: 'create' }
        }
      ]);
      return result;
    },

    duplicateCallsheet: async (workspace, request) => {
      // Fetch the original callsheet
      const originalCallsheet = await callsheetService.ensureCallsheet(
        request.originalCallsheetId
      );

      // Get linked production IDs from the original callsheet
      const linkedProductionIds =
        originalCallsheet.linked_productions?.map(lp => lp.id) ??
        (originalCallsheet.production_id
          ? [originalCallsheet.production_id]
          : []);

      // Fetch productions
      const productionResults =
        linkedProductionIds.length > 0
          ? await Promise.all(
              linkedProductionIds.map(id =>
                productionService.ensureProduction(id).catch(() => null)
              )
            )
          : [];
      const productions = productionResults.filter(
        (p): p is NonNullable<typeof p> => p !== null
      );

      // Extract booking IDs from productions for confirmed slot lookup
      const relevantBookingIds = extractBookingIdsFromProductions(productions);
      const bookingsResponse = zipObject(
        relevantBookingIds,
        await Promise.all(
          relevantBookingIds.map(id => bookingService.ensureBooking(id))
        )
      );

      // Generate duplicate data - use dateSelection from request (supports both single and range)
      const dateSelection: CallsheetDateSelection =
        request.dateSelection ??
        ({ type: 'single', date: request.fromDate } as CallsheetDateSelection);

      const duplicateInput: DuplicateCallsheetInput = {
        originalCallsheet,
        dateSelection,
        title: request.title,
        callTime: request.generalCallTime ?? undefined
      };
      const duplicateResult = generateDuplicateCallsheetData(
        duplicateInput,
        productions,
        bookingsResponse
      );

      // Create the new callsheet with all duplicated data
      const result = await interceptor.wrap(() =>
        api.createCallsheet(workspace, {
          selectedProductionIds: duplicateResult.meta.linkedProductionIds,
          title: duplicateResult.data.title,
          dateSelection,
          generalCallTime: request.generalCallTime ?? null,
          // Copy additional fields from original callsheet
          description: duplicateResult.data.description,
          note: duplicateResult.data.note,
          billing: duplicateResult.data.billing,
          social: duplicateResult.data.social,
          hard_drive: duplicateResult.data.hard_drive,
          client_logo: duplicateResult.data.client_logo,
          schedule: duplicateResult.data.schedule,
          points_of_contact: duplicateResult.data.points_of_contact,
          attachments: duplicateResult.data.attachments,
          generatedData: {
            groups: duplicateResult.data.groups,
            sections: duplicateResult.data.sections,
            team_members: duplicateResult.data.team_members,
            locations: duplicateResult.data.locations,
            recipients: duplicateResult.data.recipients
          },
          isClone: true
        })
      )();

      await reportEffectAndWaitUntilCacheReady([
        {
          entity: 'callsheet',
          entityId: result.id,
          payload: { type: 'create' }
        }
      ]);

      return result;
    },

    archiveCallsheets: interceptor.wrap(async (workspace, request) => {
      try {
        await Promise.all(
          request.ids.map(id => config.api.deleteCallsheet(id, workspace))
        );

        // Report system effects for successfully archived callsheets
        await reportEffectAndWaitUntilCacheReady(
          request.ids.map(id => ({
            entity: 'callsheet',
            entityId: id,
            payload: { type: 'update' }
          }))
        );
      } catch (error) {
        console.error('Failed to archive callsheets:', error);
        throw new Error('Failed to archive callsheets');
      }
    }),

    unarchiveCallsheets: interceptor.wrap(async (workspace, request) => {
      try {
        await Promise.all(
          request.ids.map(id => config.api.unarchiveCallsheet(id, workspace))
        );

        // Report system effects for successfully unarchived callsheets
        await reportEffectAndWaitUntilCacheReady(
          request.ids.map(id => ({
            entity: 'callsheet',
            entityId: id,
            payload: { type: 'update' }
          }))
        );
      } catch (error) {
        console.error('Failed to unarchive callsheets:', error);
        throw new Error('Failed to unarchive callsheets');
      }
    })
  };
}
